import { RefObject, useEffect, useRef, useState } from "react";

import { CARD_WITH_GUTTER_WIDTH, CONTROL_BOUNDARY } from "./constants";

/** Used by <Carousel /> component to control scrolling and scroll buttons.
 *
 * This hook is used to control the scrolling of a carousel of <Card /> components,
 * if you want to apply scrolling functionality to a different type of component,
 * you will need to refactor this hook to account for different child size widths.
 */
export function useCarouselCardScroll(
  /** A scrollable container */
  ref?: React.RefObject<HTMLElement>,
): {
  /** Previous button */
  previous: {
    scroll: () => void;
    isButtonVisible: boolean;
  };
  /** Next button */
  next: {
    scroll: () => void;
    isButtonVisible: boolean;
  };
  /** Show controls if scroll width of container is wider than client width */
  showControls: boolean;
} {
  const [showPreviousButton, setShowPreviousButton] = useState(false);
  const [showNextButton, setShowNextButton] = useState(false);

  useEffect(
    function updateButtonVisibility() {
      const container = ref?.current;
      if (!container) return;

      const onScroll = () => {
        const scrollLeft = container.scrollLeft;
        const scrollWidth = container.scrollWidth;
        const clientWidth = container.clientWidth;

        setShowPreviousButton(scrollLeft > CONTROL_BOUNDARY);
        setShowNextButton(
          scrollLeft + clientWidth < scrollWidth - CONTROL_BOUNDARY,
        );
      };

      onScroll(); // Initial visibility check

      container.addEventListener("scroll", onScroll);

      return () => {
        container?.removeEventListener("scroll", onScroll);
      };
    },
    [ref],
  );

  function scrollToPrevious() {
    if (!ref?.current) return;

    const previousCardIndex =
      Math.ceil(ref.current.scrollLeft / CARD_WITH_GUTTER_WIDTH) - 1;

    ref.current.scrollTo({
      left: previousCardIndex * CARD_WITH_GUTTER_WIDTH,
      behavior: "smooth",
    });
  }

  function scrollToNext() {
    if (!ref?.current) return;

    const nextCardIndex =
      Math.floor(ref.current.scrollLeft / CARD_WITH_GUTTER_WIDTH) + 1;

    ref.current.scrollTo({
      left: nextCardIndex * CARD_WITH_GUTTER_WIDTH,
      behavior: "smooth",
    });
  }

  return {
    previous: {
      isButtonVisible: showPreviousButton,
      scroll: scrollToPrevious,
    },
    next: {
      isButtonVisible: showNextButton,
      scroll: scrollToNext,
    },
    showControls:
      Boolean(ref?.current) &&
      ref!.current!.scrollWidth > ref!.current!.clientWidth,
  };
}

/** Monitor parent element size changes */
export function useParentElementSize(): {
  /** Ref to attach to child element */
  childRef: RefObject<HTMLDivElement>;
  /** Size of the parent element */
  parent: { element?: HTMLElement; width: number; height: number };
} {
  const childRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(function detectParentElementSize() {
    const parentElement = childRef.current?.parentElement || null;

    const updateSize = () => {
      if (parentElement) {
        setSize({
          width: parentElement?.clientWidth,
          height: parentElement?.clientHeight,
        });
      }
    };

    const resizeObserver = new ResizeObserver(updateSize);

    if (parentElement) {
      resizeObserver.observe(parentElement);
    }

    return () => {
      if (parentElement) {
        resizeObserver.unobserve(parentElement);
      }
    };
  }, []); // Empty dependency array means this effect runs once on mount

  return {
    childRef,
    parent: {
      width: size.width,
      height: size.height,
      element: childRef.current?.parentElement || undefined,
    },
  };
}
